import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { Select } from "../components/Select";
import { useAuth } from "../contexts/AuthContext";
import { getApiUrl } from "../config/api";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  role: "user" | "owner" | "admin" | "superadmin";
  isActive: boolean;
  createdAt: string;
  subscriptionStatus?: "active" | "none";
  subscriptionEndAt?: string | null;
  subscriptionPlan?: string | null;
}

export const UsersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const { authFetch } = useAuth();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [formCompanyName, setFormCompanyName] = useState("");
  const [formRole, setFormRole] = useState<
    "user" | "owner" | "admin" | "superadmin"
  >("user");
  const [formIsActive, setFormIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);

  // Edit form state
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editRole, setEditRole] = useState<
    "user" | "owner" | "admin" | "superadmin"
  >("user");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editPassword, setEditPassword] = useState("");
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const resetForm = () => {
    setFormEmail("");
    setFormPassword("");
    setFormFirstName("");
    setFormLastName("");
    setFormCompanyName("");
    setFormRole("user");
    setFormIsActive(true);
    setSubmitError(null);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authFetch(getApiUrl("/api/admin/users"));
        if (!res.ok) {
          throw new Error("Не удалось загрузить пользователей");
        }
        const data = await res.json();
        setUsers(
          (data as any[]).map((u) => ({
            id: u.id,
            email: u.email,
            firstName: u.firstName || "",
            lastName: u.lastName || "",
            companyName: u.companyName || "",
            role: u.role,
            isActive: !!u.isActive,
            createdAt: u.createdAt,
            subscriptionStatus: u.subscriptionStatus,
            subscriptionEndAt: u.subscriptionEndAt,
            subscriptionPlan: u.subscriptionPlan,
          }))
        );
      } catch (e: any) {
        setError(e?.message || "Ошибка загрузки");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [authFetch]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      const matchesSearch =
        fullName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.companyName.toLowerCase().includes(term);
      const matchesRole = selectedRole === "all" || user.role === selectedRole;
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, selectedRole]);

  const getActiveBadge = (active: boolean) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
    >
      {active ? "Активен" : "Неактивен"}
    </span>
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await authFetch(getApiUrl("/api/admin/users"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formEmail,
          password: formPassword,
          firstName: formFirstName || undefined,
          lastName: formLastName || undefined,
          companyName: formCompanyName || undefined,
          role: formRole,
          isActive: formIsActive,
        }),
      });
      if (!res.ok) {
        let message = "Не удалось создать пользователя";
        try {
          const data = await res.json();
          message = data?.message || message;
        } catch {}
        throw new Error(message);
      }
      const created = await res.json();
      setUsers((prev) => [created, ...prev]);
      setIsCreateOpen(false);
      resetForm();
    } catch (err: any) {
      setSubmitError(err?.message || "Ошибка");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openView = (user: UserRow) => {
    setSelectedUser(user);
    setIsViewOpen(true);
  };

  const openEdit = (user: UserRow) => {
    setSelectedUser(user);
    setEditFirstName(user.firstName);
    setEditLastName(user.lastName);
    setEditCompanyName(user.companyName);
    setEditRole(user.role);
    setEditIsActive(user.isActive);
    setEditPassword("");
    setEditError(null);
    setIsEditOpen(true);
  };

  const handleDelete = async (user: UserRow) => {
    if (!window.confirm(`Удалить пользователя ${user.email}?`)) return;
    try {
      const res = await authFetch(getApiUrl(`/api/admin/users/${user.id}`), {
        method: "DELETE",
      });
      if (!res.ok) {
        let msg = "Не удалось удалить пользователя";
        try {
          const data = await res.json();
          msg = data?.message || msg;
        } catch {}
        throw new Error(msg);
      }
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e: any) {
      alert(e?.message || "Ошибка удаления");
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setIsEditSubmitting(true);
    setEditError(null);
    try {
      const payload: any = {
        firstName: editFirstName || undefined,
        lastName: editLastName || undefined,
        companyName: editCompanyName || undefined,
        role: editRole,
        isActive: editIsActive,
      };
      if (editPassword) {
        payload.password = editPassword;
      }
      const res = await authFetch(
        getApiUrl(`/api/admin/users/${selectedUser.id}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        let msg = "Не удалось обновить пользователя";
        try {
          const data = await res.json();
          msg = data?.message || msg;
        } catch {}
        throw new Error(msg);
      }
      const updated = (await res.json()) as UserRow;
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setIsEditOpen(false);
      setSelectedUser(null);
    } catch (e: any) {
      setEditError(e?.message || "Ошибка");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Пользователи</h1>
            <p className="mt-1 text-sm text-gray-500">
              Управление пользователями системы
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              className="btn-primary flex items-center"
              onClick={() => setIsCreateOpen(true)}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Добавить пользователя
            </button>
          </div>
        </div>

        {/* View modal */}
        {isViewOpen && selectedUser ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-soft">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Информация о пользователе
                </h3>
                <button
                  onClick={() => {
                    setIsViewOpen(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <div>
                  <span className="text-gray-500">ID:</span> {selectedUser.id}
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>{" "}
                  {selectedUser.email}
                </div>
                <div>
                  <span className="text-gray-500">Имя:</span>{" "}
                  {selectedUser.firstName || "—"}
                </div>
                <div>
                  <span className="text-gray-500">Фамилия:</span>{" "}
                  {selectedUser.lastName || "—"}
                </div>
                <div>
                  <span className="text-gray-500">Компания:</span>{" "}
                  {selectedUser.companyName || "—"}
                </div>
                <div>
                  <span className="text-gray-500">Роль:</span>{" "}
                  {selectedUser.role}
                </div>
                <div>
                  <span className="text-gray-500">Активен:</span>{" "}
                  {selectedUser.isActive ? "Да" : "Нет"}
                </div>
                <div>
                  <span className="text-gray-500">Создан:</span>{" "}
                  {new Date(selectedUser.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Edit modal */}
        {isEditOpen && selectedUser ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-soft">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Редактировать пользователя
                </h3>
                <button
                  onClick={() => {
                    setIsEditOpen(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              {editError ? (
                <div className="mb-4 rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                  {editError}
                </div>
              ) : null}
              <form
                onSubmit={handleEditSubmit}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              >
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Имя
                  </label>
                  <input
                    className="input-field"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Фамилия
                  </label>
                  <input
                    className="input-field"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm text-gray-700">
                    Компания
                  </label>
                  <input
                    className="input-field"
                    value={editCompanyName}
                    onChange={(e) => setEditCompanyName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Роль
                  </label>
                  <select
                    className="input-field"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value as any)}
                  >
                    <option value="user">user</option>
                    <option value="owner">owner</option>
                    <option value="admin">admin</option>
                    <option value="superadmin">superadmin</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Активен
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="edit-active"
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                    />
                    <label
                      htmlFor="edit-active"
                      className="text-sm text-gray-700"
                    >
                      Активен
                    </label>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm text-gray-700">
                    Новый пароль (необязательно)
                  </label>
                  <input
                    className="input-field"
                    type="password"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setIsEditOpen(false);
                      setSelectedUser(null);
                    }}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isEditSubmitting}
                  >
                    {isEditSubmitting ? "Сохранение..." : "Сохранить"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
        {/* Filters */}
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Поиск по имени или email..."
                  className="input-field pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="sm:w-60">
              <Select
                value={selectedRole}
                onChange={setSelectedRole}
                options={[
                  { value: "all", label: "Все роли" },
                  { value: "admin", label: "Администраторы" },
                  { value: "superadmin", label: "Суперадмины" },
                  { value: "owner", label: "Владельцы" },
                  { value: "user", label: "Пользователи" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Create User Modal */}
        {isCreateOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-soft">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Добавить пользователя
                </h3>
                <button
                  onClick={() => {
                    setIsCreateOpen(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              {submitError ? (
                <div className="mb-4 rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                  {submitError}
                </div>
              ) : null}
              <form
                onSubmit={handleCreate}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              >
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm text-gray-700">
                    Email
                  </label>
                  <input
                    className="input-field"
                    type="email"
                    required
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm text-gray-700">
                    Пароль
                  </label>
                  <input
                    className="input-field"
                    type="password"
                    required
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Имя
                  </label>
                  <input
                    className="input-field"
                    value={formFirstName}
                    onChange={(e) => setFormFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Фамилия
                  </label>
                  <input
                    className="input-field"
                    value={formLastName}
                    onChange={(e) => setFormLastName(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm text-gray-700">
                    Компания
                  </label>
                  <input
                    className="input-field"
                    value={formCompanyName}
                    onChange={(e) => setFormCompanyName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Роль
                  </label>
                  <select
                    className="input-field"
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value as any)}
                  >
                    <option value="user">user</option>
                    <option value="owner">owner</option>
                    <option value="admin">admin</option>
                    <option value="superadmin">superadmin</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-700">
                    Активен
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="active"
                      type="checkbox"
                      checked={formIsActive}
                      onChange={(e) => setFormIsActive(e.target.checked)}
                    />
                    <label htmlFor="active" className="text-sm text-gray-700">
                      Активен
                    </label>
                  </div>
                </div>
                <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setIsCreateOpen(false);
                      resetForm();
                    }}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Создание..." : "Создать"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
        {/* Users Table */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Имя
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Роль
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Активность
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Компания
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Подписка
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      Загрузка...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-sm text-red-600"
                    >
                      {error}
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      Нет данных
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-700">
                                {(user.firstName || user.email).charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName || user.lastName
                                ? `${user.firstName} ${user.lastName}`.trim()
                                : user.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(user.createdAt).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getActiveBadge(user.isActive)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.companyName || "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.subscriptionStatus === "active" ? (
                          <span>
                            {user.subscriptionPlan || "Тариф"} до{" "}
                            {user.subscriptionEndAt
                              ? new Date(
                                  user.subscriptionEndAt
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                        ) : (
                          <span className="text-gray-400">нет</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            className="text-primary-600 hover:text-primary-900"
                            onClick={() => openView(user)}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            className="text-gray-600 hover:text-gray-900"
                            onClick={() => openEdit(user)}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleDelete(user)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Показано <span className="font-medium">1</span> до{" "}
            <span className="font-medium">4</span> из{" "}
            <span className="font-medium">{users.length}</span> результатов
          </div>
          <div className="flex space-x-2">
            <button className="btn-secondary text-sm">Предыдущая</button>
            <button className="btn-primary text-sm">Следующая</button>
          </div>
        </div>
      </div>
    </Layout>
  );
};
